import { useRef, useState } from "react";
import { UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Excel-import button embedded on every data page.
 *
 * The current importer reloads the *full* workbook (idempotent). It replaces
 * every row across Sales / Milestones / Land Sales / Ongoing snapshots.
 *
 * IMPORTANT (product intent): this manual-upload path is meant to stay in the
 * app *permanently* — even after direct ERP sync is wired in — as a fallback
 * / one-off correction path. Do not remove when adding the ERP adapter.
 */
export default function ImportButton({ label = "Import Excel", onDone, testId = "page-import-btn" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const submit = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please pick an .xlsx workbook");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const target = data?.sales_target || null;
      let description;
      if (target) {
        const p = target.projects_touched?.length || 0;
        const c = target.projects_created?.length || 0;
        const w = target.window?.join(" → ") || "";
        description = `${target.sales_bookings?.inserted || 0} sales cells across ${p} projects (${w})${c ? ` · created ${c} new project${c === 1 ? "" : "s"}` : ""}`;
      } else if (data?.excel_parity && !data.excel_parity.error) {
        description = `${data.excel_parity.facilities_seeded} facilities · Opening ₹${data.excel_parity.opening_balance_cr}Cr`;
      } else {
        description = `${data.projects} projects · ${data.sales} sales · ${data.flags_generated} flags`;
      }
      toast.success(target ? "Sales targets imported" : "Workbook re-imported", { description });
      onDone?.(data);
    } catch (e) {
      toast.error("Import failed", {
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={ref}
        type="file"
        accept=".xlsx"
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => submit(e.target.files?.[0])}
      />
      <button
        data-testid={testId}
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-hairline text-sm text-ink hover:border-ink hover:bg-surface transition-colors duration-150 disabled:opacity-50"
      >
        <UploadCloud size={14} strokeWidth={1.8} />
        {busy ? "Importing…" : label}
      </button>
    </>
  );
}
